import { useCallback } from 'react';
import { useDatabaseOperations } from './useSupabaseData';
import {
  generateRandomCode,
  generateFileName,
  validateFieldInput
} from '../utils/namingToolUtils';
import logger from '../lib/logger';

// Custom hook for row operations and business logic
export const useRowOperations = (employeeMapping) => {
  const { insertRowToDatabase, updateRowInDatabase, deleteRowFromDatabase } = useDatabaseOperations();

  // Templates disabled in this build; keep empty array to avoid reference errors
  const templates = [];
  
  // History tracking stub (original hook removed in simplified persistence mode)
  const addHistoryEntry = () => Promise.resolve();
  const incrementTemplateUsage = () => Promise.resolve();

  // Add a new row. Supports optional options:
  // { defaultEmployee: string | null, selectRow: boolean }
  // defaultEmployee: pre-populate the employee field and used for history editor reference.
  // selectRow: whether to select (focus) the new row after creation (defaults to true).
  const addNewRow = useCallback(async (rows, setRows, setSelectedRowId, options = {}) => {
    const ts = new Date().toISOString();
    const tempId = Date.now();
    const { defaultEmployee = null, selectRow = true } = options;
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toLocaleDateString('en-CA');
    
    try {
      const newRow = {
        id: tempId,
        code: generateRandomCode(),
        device: '',
        client: '',
        page: '',
        section: '',
        usage: '',
        description: '',
        first: '',
        last: '',
        width: '',
        height: '',
        date: today,
        employee: defaultEmployee || '',
        fileName: '',
        driveLink: '',
        created: ts,
        updated: ts,
        history: [{ ts, field: '_init', oldValue: '', newValue: 'Row created', editor: 'System' }]
      };

      // Generate filename before saving
      newRow.fileName = generateFileName(newRow, employeeMapping);

      // Add to local state first
      setRows(prev => [...prev, newRow]);
      if (selectRow) {
        setSelectedRowId(tempId);
      }

      // Announce to screen readers
      const announcement = document.getElementById('announcements');
      if (announcement) {
        announcement.textContent = `New file record created with code ${newRow.code}`;
      }

      // Insert into database
      const dbRecord = await insertRowToDatabase(newRow);
      
      // Update local state with the database ID
      setRows(prev => prev.map(row =>
        row.id === tempId
          ? { ...row, id: dbRecord.id }
          : row
      ));
      if (selectRow) {
        setSelectedRowId(dbRecord.id);
      }

    } catch (error) {
      console.error('🔴 addNewRow - Error occurred:', error);
      
      // Remove the row from local state if database insert failed
      setRows(prev => prev.filter(row => row.id !== tempId));
      
      // Announce error to screen readers
      const announcement = document.getElementById('announcements');
      if (announcement) {
        announcement.textContent = `Error creating new row: ${error.message}`;
      }
      
      // Show error message to user
      alert(`Error creating new row: ${error.message}`);
    }
  }, [insertRowToDatabase, employeeMapping]);

  const addNewRowFromTemplate = useCallback(async (template, rows, setRows, setSelectedRowId) => {
    const ts = new Date().toISOString();
    const tempId = Date.now();
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toLocaleDateString('en-CA');
    
    try {
      // Use the template data directly from the template object to create a new row
      const templateData = template.data || template; // template might have data property or be the data itself
      
      const newRow = {
        id: tempId,
        code: generateRandomCode(),
        device: templateData?.device || '',
        client: templateData?.client || '',
        page: templateData?.page || '',
        section: templateData?.section || '',
        usage: templateData?.usage || '',
        description: templateData?.description || '',
        first: templateData?.first || '',
        last: templateData?.last || '',
        width: templateData?.width || '',
        height: templateData?.height || '',
        date: today, // Set to today's date
        employee: templateData?.employee || '',
        fileName: '',
        driveLink: '',
        created: ts,
        updated: ts,
        history: [{ ts, field: '_init', oldValue: '', newValue: `Row created from template: ${template.name}`, editor: 'System' }]
      };

      // Generate filename before saving
      newRow.fileName = generateFileName(newRow, employeeMapping);

      // Add to local state first
      setRows(prev => [...prev, newRow]);
      setSelectedRowId(tempId);

      // Announce to screen readers
      const announcement = document.getElementById('announcements');
      if (announcement) {
        announcement.textContent = `New file record created from template ${template.name} with code ${newRow.code}`;
      }

      // Insert into database
      const dbRecord = await insertRowToDatabase(newRow);
      
      // Update local state with the database ID
      setRows(prev => prev.map(row => 
        row.id === tempId 
          ? { ...row, id: dbRecord.id }
          : row
      ));

      // Increment template usage count
      try {
        await incrementTemplateUsage(template.id);
      } catch (templateError) {
        console.warn('Failed to increment template usage count:', templateError);
        // Don't fail the whole operation for this
      }

    } catch (error) {
      console.error('🔴 addNewRowFromTemplate - Error occurred:', error);
      
      // Remove the row from local state if database insert failed
      setRows(prev => prev.filter(row => row.id !== tempId));
      
      // Announce error to screen readers
      const announcement = document.getElementById('announcements');
      if (announcement) {
        announcement.textContent = `Error creating new row from template: ${error.message}`;
      }
      
      // Show error message to user
      alert(`Error creating new row from template: ${error.message}`);
    }
  }, [insertRowToDatabase, employeeMapping]);

  const duplicateRow = useCallback(async (rowToDuplicate, setRows, setHighlightCallback) => {
    const tempId = Date.now();
    const ts = new Date().toISOString();
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date().toLocaleDateString('en-CA');
    
    try {
      const newRow = {
        ...rowToDuplicate,
        id: tempId,
        code: generateRandomCode(),
        date: today, // Set to today's date
        driveLink: '', // Reset drive link for duplicated row
        created: ts,
        updated: ts,
        history: [{ ts, field: '_init', oldValue: '', newValue: 'Row duplicated', editor: 'System' }]
      };
      newRow.fileName = generateFileName(newRow, employeeMapping);

      // Add to local state first - insert right after the original row
      setRows(prev => {
        const originalIndex = prev.findIndex(row => row.id === rowToDuplicate.id);
        if (originalIndex !== -1) {
          // Insert new row right after the original
          const newRows = [...prev];
          newRows.splice(originalIndex + 1, 0, newRow);
          return newRows;
        } else {
          // Fallback: append to end if original not found
          return [...prev, newRow];
        }
      });
      setHighlightCallback(tempId);

      // Announce to screen readers
      const announcement = document.getElementById('announcements');
      if (announcement) {
        announcement.textContent = `Duplicated row ${rowToDuplicate.code} as new row ${newRow.code}`;
      }

      // Insert into database
      const dbRecord = await insertRowToDatabase(newRow);
      
      // Update local state with the database ID
      setRows(prev => prev.map(row => 
        row.id === tempId 
          ? { ...row, id: dbRecord.id }
          : row
      ));
      setHighlightCallback(dbRecord.id);

    } catch (error) {
      console.error('Error duplicating row:', error);
      // Remove the row from local state if database insert failed
      setRows(prev => prev.filter(row => row.id !== tempId));
      
      // Announce error to screen readers
      const announcement = document.getElementById('announcements');
      if (announcement) {
        announcement.textContent = `Error duplicating row: ${error.message}`;
      }
      
      alert(`Error duplicating row: ${error.message}`);
    }
  }, [insertRowToDatabase, employeeMapping]);

  const updateRow = useCallback(async (id, field, value, rows, setRows) => {
    setRows(prevRows => prevRows.map(row => {
      if (row.id !== id) return row;

      // Enhanced input validation and sanitization
      const { sanitizedValue, validationError } = validateFieldInput(field, value);

      const updatedRow = { ...row, [field]: sanitizedValue };
      
      // Add validation error to row if any
      if (validationError) {
        updatedRow.validationErrors = {
          ...updatedRow.validationErrors,
          [field]: validationError
        };
      } else {
        // Clear validation error for this field if validation passed
        if (updatedRow.validationErrors) {
          const { [field]: removed, ...remainingErrors } = updatedRow.validationErrors;
          updatedRow.validationErrors = Object.keys(remainingErrors).length > 0 ? remainingErrors : undefined;
        }
      }

      // Generate filename with enhanced options
      updatedRow.fileName = generateFileName(updatedRow, employeeMapping, {
        maxLength: 200,
        includeTimestamp: false,
        priorityFields: ['code', 'client', 'page', 'section'],
        strictMode: true
      });

      if (field !== 'updated') {
        updatedRow.updated = new Date().toISOString();
      }

        // Add history entry for significant changes (not for temporary fields)
        if (!['history', 'validationErrors'].includes(field)) {
          const historyEntry = {
            ts: updatedRow.updated,
            field,
            oldValue: String(row[field] ?? ''), // Keep original values without HTML encoding
            newValue: String(sanitizedValue ?? ''), // Keep original values without HTML encoding
            editor: updatedRow.employee || 'Unknown',
            validationError: validationError || null
          };
          updatedRow.history = [...(row.history || []), historyEntry];        // Add to persistent history tracking if row exists in database
        if (updatedRow.existsInDatabase && !updatedRow.isNew) {
          addHistoryEntry(
            field,
            String(row[field] ?? ''),
            String(sanitizedValue ?? ''),
            'update',
            updatedRow.employee || 'Unknown'
          ).catch(error => {
            logger.errorSecure('Failed to add history entry', error);
          });
        }
      }

      // Handle database operations
      if (updatedRow.isNew) {
        // This is a new row that needs to be inserted into the database
        insertRowToDatabase(updatedRow).then(dbRecord => {
          // Update the row with the database ID and remove the isNew flag
          setRows(prevRows => prevRows.map(r => 
            r.id === id 
              ? { ...r, id: dbRecord.id, isNew: undefined, existsInDatabase: true }
              : r
          ));
        }).catch(error => {
          console.error('🔴 updateRow - Error inserting new row to database:', error);
          alert(`Error saving new row: ${error.message}`);
        });
      } else {
        // Update existing row in database asynchronously (don't wait for it to avoid blocking UI)
        updateRowInDatabase(updatedRow).then(result => {
          // If the row was inserted instead of updated, update the local ID
          if (result && result.id !== updatedRow.id) {
            setRows(prevRows => prevRows.map(r => 
              r.id === id 
                ? { ...r, id: result.id, existsInDatabase: true }
                : r
            ));
          }
        }).catch(error => {
          console.error('🔴 updateRow - Error updating row in database:', error);
          
          // More specific error messages
          if (error.code === 'PGRST116') {
            // Row not found in database, will attempt to insert on next update
          } else {
            alert(`Error updating row: ${error.message}`);
          }
        });
      }

      return updatedRow;
    }));
  }, [insertRowToDatabase, updateRowInDatabase, employeeMapping]);

  const deleteRow = useCallback(async (id, rows, setRows) => {
    if (rows.length > 1) {
      try {
        // Delete from database first (unless it's a new row that hasn't been saved yet)
        const rowToDelete = rows.find(row => row.id === id);
        if (rowToDelete && !rowToDelete.isNew) {
          await deleteRowFromDatabase(id);
        }

        // Remove from local state
        setRows(prev => prev.filter(row => row.id !== id));
      } catch (error) {
        console.error('Error deleting row:', error);
        // You might want to show an error message to the user here
      }
    }
  }, [deleteRowFromDatabase]);

  // Image upload functionality removed - now using Drive Link field

  return {
    addNewRow,
    addNewRowFromTemplate,
    duplicateRow,
    updateRow,
    deleteRow,
    templates
  };
};